import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';

/**
 * Identity + role payload the TAS API needs on every state-transition
 * to attribute the action. Both apps build this same shape — they just
 * source `actingWithRole` from different places:
 *
 * - **admin**: heuristic on `user.isTasTioApprover`. If true, the user
 *   is acting via their EMPLOYEE delegation as `TAS.Approver.TIO_APPROVER`;
 *   otherwise as a plain TIO_ADMIN. The endpoints reject the call if
 *   the role isn't valid, so this heuristic is safe-ish but is the
 *   first thing to look at when a transition is rejected unexpectedly.
 * - **employee**: reads `sessionContext.currentRole` directly — the role
 *   the user picked at login (or auto-selected on single-role accounts).
 *
 * tio-common components that don't know which app they're rendered in
 * call `buildTasActorFromSession(this.sessionContext)` and let the
 * dispatcher pick based on whether `currentRole` is set (employee
 * only). Per-app callers that already know their context use
 * `buildTasAdminActor(user)` or `buildTasEmployeeActor(user, currentRole)`
 * directly.
 */
export interface TasActor {
  name: string;
  userId: string;
  actingWithRole: { role: string; relationshipType: string };
}

/** @deprecated Prefer {@link TasActor}; alias kept for incremental renames. */
export type TasAdminActor = TasActor;

export function buildTasAdminActor(user: UserModel): TasActor {
  const isTasTioApprover = user.isTasTioApprover;
  return {
    name: user.person?.fullName ?? '',
    userId: (user.id as string) ?? '',
    actingWithRole: {
      role: isTasTioApprover ? 'EMPLOYEE' : 'TIO_ADMIN',
      relationshipType: isTasTioApprover ? 'TAS.Approver.TIO_APPROVER' : 'TIO_ADMIN',
    },
  };
}

/**
 * Minimum shape buildTasEmployeeActor needs from a Role record. Stays
 * structural so the helper doesn't have to import the per-app Role
 * type (employee's session-context types `currentRole` from its own
 * role model).
 */
export interface TasEmployeeRole {
  role?: string;
  relationshipType?: string;
}

export function buildTasEmployeeActor(
  user: UserModel,
  currentRole: TasEmployeeRole | undefined
): TasActor {
  return {
    name: user.person?.fullName ?? '',
    userId: (user.id as string) ?? '',
    actingWithRole: {
      role: currentRole?.role ?? '',
      relationshipType: currentRole?.relationshipType ?? '',
    },
  };
}

/**
 * tio-common dispatcher: returns the right actor shape from whatever
 * sessionContext is available at runtime. If `currentRole` is set
 * (tio-employee), uses the employee builder; otherwise (tio-admin)
 * applies the isTasTioApprover heuristic.
 */
export interface TasActorSessionShape {
  user: UserModel;
  currentRole?: TasEmployeeRole;
}

export function buildTasActorFromSession(session: TasActorSessionShape): TasActor {
  if (session.currentRole) {
    return buildTasEmployeeActor(session.user, session.currentRole);
  }
  return buildTasAdminActor(session.user);
}
